const maxStringLength = 8000;
const sensitiveKeyPattern = /(password|token|secret|authorization|cookie|email|phone|address)/i;

function truncateString(value) {
  if (value.length <= maxStringLength) {
    return value;
  }

  return value.slice(0, maxStringLength) + '...[truncated]';
}

export function sanitizeValue(value, depth = 0) {
  if (depth > 4) {
    return '[max-depth]';
  }

  if (typeof value === 'string') {
    return truncateString(value);
  }

  if (Array.isArray(value)) {
    return value.slice(0, 30).map((item) => sanitizeValue(item, depth + 1));
  }

  if (value && typeof value === 'object') {
    return Object.keys(value).slice(0, 60).reduce((result, key) => {
      if (sensitiveKeyPattern.test(key)) {
        result[key] = '[redacted]';
        return result;
      }

      try {
        result[key] = sanitizeValue(value[key], depth + 1);
      } catch (error) {
        result[key] = '[unreadable]';
      }

      return result;
    }, {});
  }

  return value;
}

function getSafeKvConfig() {
  const kvConfig = document.kvConfig || {};
  const safeKeys = [
    'userId',
    'jobId',
    'visualizerType',
    'navigationType',
    'toolVersion',
    'subDomain',
    'domain',
    'kioskMode',
    'noInternetMode',
    'offlineMode',
    'isBackend',
    'devMode',
    'visualizerOnly',
    'layersSize',
    'useLocalComposer',
  ];

  return safeKeys.reduce((result, key) => {
    if (kvConfig[key] !== undefined) {
      result[key] = sanitizeValue(kvConfig[key]);
    }

    return result;
  }, {});
}

function getBrowserContext() {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio,
    },
    timezone: typeof Intl !== 'undefined' && Intl.DateTimeFormat ? Intl.DateTimeFormat().resolvedOptions().timeZone : null,
    online: navigator.onLine,
  };
}

export function errorToPlainObject(error) {
  if (!error) {
    return {};
  }

  if (typeof error === 'string') {
    return {
      message: error,
    };
  }

  return {
    name: error.name,
    message: error.message || String(error),
    stack: error.stack,
  };
}

function getStackPreview(stack) {
  if (!stack) {
    return null;
  }

  return String(stack).split('\n').slice(0, 4).join('\n');
}

function buildSummary(type, message, kvConfig, url) {
  const userId = kvConfig.userId || 'unknown-user';
  const visualizerType = kvConfig.visualizerType || 'unknown-visualizer';

  return type + ' | ' + message + ' | userId=' + userId + ' | visualizer=' + visualizerType + ' | ' + url;
}

// Normalizes browser/runtime error shapes into the stable frontend error contract.
export function buildErrorPayload({type, errorData, extra}) {
  const kvConfig = getSafeKvConfig();
  const browser = getBrowserContext();
  const url = window.location.href;
  const message = errorData.message || 'Unknown frontend error';

  return sanitizeValue({
    type,
    level: 'fatal',
    message,
    summary: buildSummary(type, message, kvConfig, url),
    name: errorData.name,
    stack: errorData.stack,
    stackPreview: getStackPreview(errorData.stack),
    source: errorData.source,
    line: errorData.line,
    column: errorData.column,
    componentStack: extra && extra.componentStack,
    app: 'visualizer',
    userId: kvConfig.userId,
    visualizerType: kvConfig.visualizerType,
    navigationType: kvConfig.navigationType,
    release: document.kvConfig && document.kvConfig.frontendRelease,
    url,
    browser,
    kvConfig,
    timestamp: new Date().toISOString(),
  });
}
